<!--
// app/pages/[region]/Tables.vue
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type { StandingPlayoffColors } from "~/src/season-cluster/domain/models/01.Season";
import { useRegions } from "~/app/composables/regions";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useSeasons } from "~/app/composables/season/seasons";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import CurrentSeasonSliderMatches from "~/app/components/Shared/slider-matches/CurrentSeasonSliderMatches.vue";
import GridPartnersCompetition from "~/app/components/Shared/GridPartnersCompetition.vue";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import BracketTablesWorldCup from "~/app/components/pages/region-world-cup/BracketTablesWorldCup.vue";
import BracketTablesNations from "~/app/components/pages/region-nations/BracketTablesNations.vue";
import GermanyBracket from "~/app/components/pages/region-home/GermanyBracket.vue";
import TableStandingRegularPhase from "~/app/components/pages/region-tables/TableStandingRegularPhase.vue";
import GridPlayoffPhase from "~/app/components/pages/region-tables/GridPlayoffPhase.vue";
import MetaBracketPlayoff from "~/app/components/pages/region-tables/MetaBracketPlayoff.vue";
import { useBracketMetaDetection } from "~/app/composables/bracket/useBracketMetaDetection";

const localePath = useLocalePath();

const { t } = useI18n();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { isWorldCup, isNations, isSpain, isAmericas, regionParam } = useRegions();
const { region, currentSeason } = useCompetitionStore();
const { getSeasonStanding } = useSeasons();
const { contextSiteTitle, isKings, isQueens } = useWebContext();
const { hasBracketMetaConfig } = useBracketMetaDetection();

const standingPhase = ref<Phase>();
const playoffPhase = ref<Phase>();
const loading = ref<boolean>(false);
const positionLegend = ref<StandingPlayoffColors[] | StandingPlayoffColors[][]>();
const secondaryPositionLegend = ref<StandingPlayoffColors[] | StandingPlayoffColors[][]>();
const winnerTeam = ref<number | null>();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/tables",
    es: "/[region]/clasificacion",
    de: "/[region]/klassifikation",
    fr: "/[region]/classement",
    it: "/[region]/classifica",
    pt: "/[region]/classificacao",
    ar: "/[region]/tables",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

async function updateStandingPhase(): Promise<Phase | void> {
  try {
    loading.value = true;
    const _seasonId = seasonId.value;
    if (!_seasonId) return;

    const {
      phase,
      playoff,
      winnerTeamId,
      positionLegend: legend,
      secondaryPositionLegend: secondaryLegend,
    } = await getSeasonStanding(_seasonId);
    standingPhase.value = phase;
    positionLegend.value = legend;
    secondaryPositionLegend.value = secondaryLegend;
    playoffPhase.value = playoff;
    winnerTeam.value = winnerTeamId;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

const showPlayoffPhase = computed(() => {
  if (!playoffPhase.value) return false;
  return playoffPhase.value.groups.some(group => {
    return group.turns.some(turn => !!turn.matches.length);
  });
});

const hasBracketMeta = computed(() => {
  return hasBracketMetaConfig(playoffPhase.value);
});

if (seasonId.value) {
  await updateStandingPhase();
}

useSeoMeta({
  title: t("menuItem.tables"),
  ogTitle: t("menuItem.tables"),
  description: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <header-banner-title :title="$t('tables.banner.title')" />

  <section v-if="!isWorldCup && !isNations" class="kql-w-container px-4">
    <div class="flex flex-col bigsm:flex-row items-center justify-start gap-2 bigsm:justify-start">
      <nuxt-link
        v-if="seasonClusterId === 2 && isKings && (isSpain || isAmericas)"
        :to="localePath({ name: 'region-Tables', params: { region: 'world-cup' } })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to World Cup</nuxt-link
      >
      <nuxt-link
        v-else-if="seasonClusterId === 2 && isQueens"
        :to="localePath({ name: 'FinalissimaQueens' })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to Finalissima</nuxt-link
      >
      <div class="w-full bigsm:ml-auto">
        <seasons-filter
          v-model:season-id="seasonId"
          v-model:season-cluster-id="seasonClusterId"
          @change="updateStandingPhase"
        />
      </div>
    </div>

    <div v-if="seasonId === 36">
      <germany-bracket />
    </div>

    <div v-else>
      <table-standing-regular-phase
        :season-id="seasonId"
        :loading="loading"
        :position-legend="positionLegend"
        :secondary-position-legend="secondaryPositionLegend"
        :standing-phase="standingPhase"
      />
      <!-- Priority: 1) MetaBracket (from API metaInformation), 2) GridPlayoffPhase (default fallback) -->
      <meta-bracket-playoff
        v-if="showPlayoffPhase && hasBracketMeta"
        :playoff-phase="playoffPhase"
        :season-id="seasonId"
        :winner-team-id="winnerTeam"
        :region="regionParam"
      />
      <grid-playoff-phase
        v-else-if="showPlayoffPhase"
        :playoff-phase="playoffPhase"
        :season-id="seasonId"
        :winner-team-id="winnerTeam"
      />
    </div>
  </section>

  <section v-if="isWorldCup" class="kql-w-container px-4">
    <bracket-tables-world-cup />
  </section>

  <section v-else-if="isNations" class="kql-w-container px-4">
    <bracket-tables-nations />
  </section>

  <section v-if="!isWorldCup && !isNations" class="py-8">
    <current-season-slider-matches section-title with-add-to-calendar-in-section />
  </section>

  <grid-partners-competition />
</template>

<style scoped></style>
